/**
 * React Query mutation hook for deleting a payment record
 * Handles payment record deletion with automatic query invalidation
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deletePaymentRecord } from 'services/billingServices';
import { queryKeys } from '../../../config/queryClient';

/**
 * Hook to delete a payment record using React Query mutation
 *
 * @param {Object} options - Configuration options
 * @param {Function} options.onSuccess - Callback function called on successful deletion
 * @param {Function} options.onError - Callback function called on deletion error
 *
 * @returns {Object} Mutation object
 * @returns {Function} returns.mutate - Function to trigger the mutation
 * @returns {Function} returns.mutateAsync - Async function to trigger the mutation
 * @returns {boolean} returns.isLoading - Loading state
 * @returns {boolean} returns.isError - Error state
 * @returns {Object|null} returns.error - Error object if mutation failed
 *
 * @example
 * const deletePaymentRecordMutation = useDeletePaymentRecordQuery({
 *   onSuccess: () => {
 *     console.log('Payment record deleted successfully');
 *   },
 *   onError: (error) => {
 *     console.error('Failed to delete payment record:', error);
 *   },
 * });
 *
 * // Usage
 * deletePaymentRecordMutation.mutate({
 *   payment_record_sk: 'PAYMENT_RECORD#123',
 *   is_lead: false,
 * });
 */
export const useDeletePaymentRecordQuery = ({ onSuccess, onError } = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ payment_record_sk, is_lead = false }) => {
      return deletePaymentRecord(payment_record_sk, is_lead);
    },
    onSuccess: (data, variables, context) => {
      // Invalidate all payment history queries to refresh the list
      queryClient.invalidateQueries({ queryKey: queryKeys.paymentHistory.all });

      // Call custom onSuccess callback if provided
      if (onSuccess) {
        onSuccess(data, variables, context);
      }
    },
    onError: (error, variables, context) => {
      // Call custom onError callback if provided
      if (onError) {
        onError(error, variables, context);
      }
    },
  });
};

export default useDeletePaymentRecordQuery;
